/**
 * useHrOffboarding — Admin-Web hooks for Issue #363 (HR offboarding lifecycle,
 * access revoke, handoff checklist, payroll hold).
 *
 * 红线 (AI GUIDE §6):
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch、不构造前端假状态。
 *   - 列表 / 详情 / 事件不携带姓名、邮箱、手机号、薪资明细；UI 只展示 id /
 *     状态 / 时间。
 *   - 409 提示乐观锁冲突；403 提示权限不足；统一信号供页面渲染。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type HrOffboardingCaseDto =
  ApiComponents['schemas']['HrOffboardingCaseDto'];
export type HrOffboardingCaseDetailDto =
  ApiComponents['schemas']['HrOffboardingCaseDetailDto'];
export type HrOffboardingCaseListDto =
  ApiComponents['schemas']['HrOffboardingCaseListDto'];
export type HrOffboardingHandoffItemDto =
  ApiComponents['schemas']['HrOffboardingHandoffItemDto'];
export type HrOffboardingEventDto =
  ApiComponents['schemas']['HrOffboardingEventDto'];
export type HrOffboardingEventListDto =
  ApiComponents['schemas']['HrOffboardingEventListDto'];
export type CreateHrOffboardingCaseDto =
  ApiComponents['schemas']['CreateHrOffboardingCaseDto'];
export type AddHrOffboardingHandoffItemDto =
  ApiComponents['schemas']['AddHrOffboardingHandoffItemDto'];
export type UpdateHrOffboardingHandoffItemDto =
  ApiComponents['schemas']['UpdateHrOffboardingHandoffItemDto'];
export type SubmitOffboardingForFinalReviewDto =
  ApiComponents['schemas']['SubmitOffboardingForFinalReviewDto'];
export type RecordOffboardingFinalReviewDto =
  ApiComponents['schemas']['RecordOffboardingFinalReviewDto'];
export type CancelOffboardingCaseDto =
  ApiComponents['schemas']['CancelOffboardingCaseDto'];
export type HrOffboardingDuePlannedExecutionListDto =
  ApiComponents['schemas']['HrOffboardingDuePlannedExecutionListDto'];

export interface HrOffboardingApiError {
  readonly message: string;
  readonly status?: number;
}

function toApiError(err: unknown, status?: number): HrOffboardingApiError {
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或状态不符，请刷新后重试',
      status,
    };
  }
  if (status === 403) return { message: '没有权限执行此操作', status };
  if (status === 400) {
    return { message: '请求参数不合法或交接清单未达到提交条件', status };
  }
  if (status === 404) return { message: '案件不存在或不可访问', status };
  if (err instanceof Error) return { message: err.message, status };
  return { message: String(err), status };
}

export interface ListHrOffboardingFilter {
  employeeId?: string;
  userId?: string;
  mode?: HrOffboardingCaseDto['mode'];
  status?: HrOffboardingCaseDto['status'];
  payrollHoldStatus?: HrOffboardingCaseDto['payrollHoldStatus'];
  page?: number;
  limit?: number;
}

export function useHrOffboardingCases() {
  const [items, setItems] = useState<HrOffboardingCaseDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrOffboardingApiError | null>(null);

  const fetchCases = useCallback(
    async (filter?: ListHrOffboardingFilter) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.employeeId) query.employeeId = filter.employeeId;
        if (filter?.userId) query.userId = filter.userId;
        if (filter?.mode) query.mode = filter.mode;
        if (filter?.status) query.status = filter.status;
        if (filter?.payrollHoldStatus) {
          query.payrollHoldStatus = filter.payrollHoldStatus;
        }
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/offboarding-cases',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { items, total, loading, error, fetchCases };
}

export function useHrOffboardingDetail() {
  const [detail, setDetail] = useState<HrOffboardingCaseDetailDto | null>(null);
  const [events, setEvents] = useState<HrOffboardingEventDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrOffboardingApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/offboarding-cases/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setDetail(data);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async (id: string) => {
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/offboarding-cases/{id}/events',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setEvents([...data.items]);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    }
  }, []);

  return { detail, events, loading, error, fetchDetail, fetchEvents };
}

export function useHrOffboardingMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrOffboardingApiError | null>(null);

  const createCase = useCallback(async (body: CreateHrOffboardingCaseDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/hr/offboarding-cases',
        { body },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const addHandoffItem = useCallback(
    async (id: string, body: AddHrOffboardingHandoffItemDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/offboarding-cases/{id}/handoff-items',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateHandoffItem = useCallback(
    async (
      id: string,
      itemId: string,
      body: UpdateHrOffboardingHandoffItemDto,
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/offboarding-cases/{id}/handoff-items/{itemId}/update',
          { params: { path: { id, itemId } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const submitFinalReview = useCallback(
    async (id: string, body: SubmitOffboardingForFinalReviewDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/offboarding-cases/{id}/submit-final-review',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const recordFinalReview = useCallback(
    async (id: string, body: RecordOffboardingFinalReviewDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/offboarding-cases/{id}/final-review',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancelCase = useCallback(
    async (id: string, body: CancelOffboardingCaseDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/offboarding-cases/{id}/cancel',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const processDuePlanned = useCallback(
    async (options?: { dryRun?: boolean }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/offboarding-cases/process-due-planned',
          { body: { dryRun: options?.dryRun ?? false } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    createCase,
    addHandoffItem,
    updateHandoffItem,
    submitFinalReview,
    recordFinalReview,
    cancelCase,
    processDuePlanned,
  };
}

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------

export const HR_OFFBOARDING_MODE_LABELS: Record<
  HrOffboardingCaseDto['mode'],
  string
> = {
  planned: '计划离职',
  immediate: '立即离职',
};

export const HR_OFFBOARDING_CASE_STATUS_LABELS: Record<
  HrOffboardingCaseDto['status'],
  string
> = {
  draft: '草稿',
  scheduled: '已排期',
  in_progress: '执行中',
  pending_final_review: '待终审',
  completed: '已完成',
  cancelled: '已取消',
  blocked: '阻塞中',
};

export const HR_OFFBOARDING_PAYROLL_HOLD_LABELS: Record<
  HrOffboardingCaseDto['payrollHoldStatus'],
  string
> = {
  pending_review: '待复核',
  payout_blocked: '暂缓发放',
  release_allowed: '允许发放',
  retained_blocked: '终审扣留',
};

export const HR_OFFBOARDING_HANDOFF_TYPE_LABELS: Record<
  HrOffboardingHandoffItemDto['type'],
  string
> = {
  asset_return: '资产归还',
  customer_handoff: '客户/合同移交',
  file_transfer: '文档/文件转交',
  task_handoff: '任务移交',
  permission_revoke: '权限回收复核',
  compliance_review: '合规与政策复核',
  other: '其它',
};

export const HR_OFFBOARDING_HANDOFF_STATUS_LABELS: Record<
  HrOffboardingHandoffItemDto['status'],
  string
> = {
  pending: '待办',
  in_progress: '进行中',
  completed: '已完成',
  waived: '已豁免',
  blocked: '阻塞中',
};
